import { from, ApolloClient, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { commonAuthHeaders } from "../auth/utils";

const errorLink = onError(({ graphQLErrors, networkError }) => {
 
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => { 
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        
      )
    }

    );

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    if (networkError?.statusCode === 401) {
      window.location.href = '/signin';
    }
  }
});

const httpLink = createUploadLink({
  uri: `${process.env.REACT_APP_APIS_URL}/gq/graphql`,
  credentials: 'include'
});

const authLink = setContext((_, { headers }) => {
  return new Promise(async (resolve, reject) => {
    // get the authentication headers
    const updatedHeaders = await commonAuthHeaders(headers)
    resolve({
      headers: updatedHeaders
    }
    )
  });
});

// Helper function to safely clone objects to prevent immutability issues
const safeClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.warn('Failed to clone object, returning original:', e);
    return obj;
  }
};

const paginationMergeProducts = {
  keyArgs: false,
  merge(existing, incoming, { args }) {
    try {
      if (args) {
        const { from = 0 } = args;
        if (!from || !existing) {
          // if from is 0 (start position of pagination)
          return safeClone(incoming);
        }
        const results =
          (existing.hits &&
            existing.hits.results &&
            existing.hits.results.slice(0)) ||
          [];
        const incomingResults = (incoming.hits && incoming.hits.results) || [];
        for (let i = 0; i < incomingResults.length; ++i) {
          results[from + i] = safeClone(incomingResults[i]);
        }
        return {
          facets: safeClone(incoming.facets),
          hits: {
            total: incoming.hits.total,
            results: results,
          },
        };
      } else {
        // It's unusual (probably a mistake) for a paginated field not
        // to receive any arguments, so you might prefer to throw an
        // exception here, instead of recovering by appending incoming
        // onto the existing array.
        console.warn("[Bimmatch] Apollo Client merge issue - no args provided");
        return safeClone(incoming);
      }
    } catch (error) {
      console.error("[Bimmatch] Error in pagination merge:", error);
      return safeClone(incoming);
    }
  },
};

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        search: paginationMergeProducts,
        searchOrganizationObjects: paginationMergeProducts
      },
    },
    SearchFacet: {
      // Search Facets are always new for new search
      fields: {
        buckets: {
          keyArgs: false,
          merge(existing, incoming) {
            // Search Facets are always new for new search, so just replace with incoming buckets
            return safeClone(incoming);
          },
        },
      },
    },
  },
  addTypename: false,
  // Prevent object freezing and canonization issues that cause extensibility errors
  resultCaching: false,
  canonizeResults: false,
  // Ensure all objects are treated as mutable
  freezeResults: false,
});

export const client = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache,
  connectToDevTools: process.env.REACT_APP_ENV !== "production",
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
    },
  },
});
